import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { Card } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { DataTable, Column } from "../../components/common/Table";
import { Modal } from "../../components/common/Modal";
import { useAuth } from "../../context/AuthContext";
import type { WorkOrder, WorkOrderDetail as WorkOrderDetailType, Plant, Unit } from "../../types";

interface Technician {
  id: number;
  full_name: string;
  dept_name?: string | null;
}

const WO_TYPES = ["CORRECTIVE", "PREVENTIVE", "IMPROVEMENT"];

export default function WorkOrderList() {
  const { hasPermission, user } = useAuth();
  const [rows, setRows] = useState<WorkOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [plantFilter, setPlantFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [technicianFilter, setTechnicianFilter] = useState("");
  const [plants, setPlants] = useState<Plant[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const unitsForPlant = useMemo(
    () => (plantFilter ? units.filter((u) => u.plant_id === Number(plantFilter)) : units),
    [units, plantFilter]
  );

  const loadFilters = async () => {
    try {
      const [p, u, t] = await Promise.all([
        api.get<Plant[]>("/api/master-data/plants"),
        api.get<Unit[]>("/api/master-data/units"),
        api.get<Technician[]>("/api/users/technicians"),
      ]);
      setPlants(p);
      setUnits(u);
      setTechnicians(t);
    } catch {
      // Non-fatal - filters just won't populate if this fails.
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (plantFilter) params.set("plantId", plantFilter);
      if (unitFilter) params.set("unitId", unitFilter);
      if (typeFilter) params.set("woType", typeFilter);
      if (technicianFilter) params.set("technicianId", technicianFilter);
      const qs = params.toString();
      const data = await api.get<WorkOrder[]>(`/api/work-orders${qs ? `?${qs}` : ""}`);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, plantFilter, unitFilter, typeFilter, technicianFilter]);

  const columns: Column<WorkOrder>[] = [
    { header: "WO Number", accessor: (w) => <span className="font-mono text-xs">{w.wo_number}</span> },
    { header: "Type", accessor: (w) => w.wo_type },
    { header: "Plant", accessor: (w) => w.plant_name ?? "-" },
    { header: "Unit", accessor: (w) => w.unit_name ?? "-" },
    { header: "Tag No.", accessor: (w) => w.tag_no ?? (w.asset_name ? w.asset_name : <span className="text-slate-400">(unit-level)</span>) },
    { header: "Priority", accessor: (w) => <Badge value={w.priority} /> },
    { header: "Status", accessor: (w) => <Badge value={w.status} /> },
    { header: "Technician", accessor: (w) => w.technician_name ?? "Unassigned" },
    { header: "Downtime", accessor: (w) => (w.downtime_minutes ? `${Math.round(w.downtime_minutes)} min` : "-") },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Work Orders</h1>
        <p className="text-sm text-slate-500">Corrective, preventive, and improvement work order pipeline.</p>
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          {["", "OPEN", "ASSIGNED", "IN_PROGRESS", "COMPLETED", "VERIFIED", "CLOSED"].map((s) => (
            <button
              key={s || "ALL"}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                statusFilter === s ? "bg-accent-500 text-white" : "bg-slate-100 dark:bg-industrial-700 text-slate-600 dark:text-slate-300"
              }`}
            >
              {s || "All"}
            </button>
          ))}
        </div>

        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <select
            className="input-field"
            value={plantFilter}
            onChange={(e) => {
              setPlantFilter(e.target.value);
              setUnitFilter("");
            }}
          >
            <option value="">All Plants</option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.plant_name}
              </option>
            ))}
          </select>
          <select className="input-field" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
            <option value="">All Units</option>
            {unitsForPlant.map((u) => (
              <option key={u.id} value={u.id}>
                {u.unit_code} - {u.unit_name}
              </option>
            ))}
          </select>
          <select className="input-field" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {WO_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select className="input-field" value={technicianFilter} onChange={(e) => setTechnicianFilter(e.target.value)}>
            <option value="">All Technicians</option>
            {technicians.map((t) => (
              <option key={t.id} value={t.id}>
                {t.full_name}
              </option>
            ))}
          </select>
        </div>

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : <DataTable columns={columns} rows={rows} keyField={(w) => w.id} onRowClick={(w) => setSelectedId(w.id)} />}
      </Card>

      <Modal open={selectedId !== null} onClose={() => setSelectedId(null)} title={rows.find((r) => r.id === selectedId)?.wo_number ?? ""} xl>
        {selectedId !== null && (
          <WorkOrderDetailPane
            id={selectedId}
            technicians={technicians}
            canAssign={hasPermission("*", "wo:assign", "technician:assign")}
            canUpdateProgress={hasPermission("*", "wo:update_progress")}
            canRecordRepair={hasPermission("*", "wo:record_repair")}
            canVerify={hasPermission("*", "wo:close", "wo:approve")}
            canUploadPhoto={hasPermission("*", "wo:upload_photo")}
            canCloseAll={hasPermission("*", "wo:close")}
            canCloseAssigned={hasPermission("*", "wo:close_assigned")}
            currentUserId={user?.id}
            onChanged={() => {
              load();
            }}
          />
        )}
      </Modal>
    </div>
  );
}

/** Fetches the full Work Order detail (images, checklist) on open, since the
 *  list query doesn't include those - then renders the same actions the old
 *  inline detail view had. */
function WorkOrderDetailPane({
  id,
  technicians,
  canAssign,
  canUpdateProgress,
  canRecordRepair,
  canVerify,
  canUploadPhoto,
  canCloseAll,
  canCloseAssigned,
  currentUserId,
  onChanged,
}: {
  id: number;
  technicians: Technician[];
  canAssign: boolean;
  canUpdateProgress: boolean;
  canRecordRepair: boolean;
  canVerify: boolean;
  canUploadPhoto: boolean;
  canCloseAll: boolean;
  canCloseAssigned: boolean;
  currentUserId?: number;
  onChanged: () => void;
}) {
  const [wo, setWo] = useState<WorkOrderDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<WorkOrderDetailType>(`/api/work-orders/${id}`);
      setWo(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load work order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <p className="text-sm text-slate-400">Loading…</p>;
  if (error || !wo) return <p className="text-sm text-red-600">{error ?? "Not found"}</p>;

  const canClose = canCloseAll || (canCloseAssigned && wo.assigned_technician_id === currentUserId);

  return (
    <WorkOrderDetail
      wo={wo}
      technicians={technicians}
      canAssign={canAssign}
      canUpdateProgress={canUpdateProgress}
      canRecordRepair={canRecordRepair}
      canVerify={canVerify}
      canUploadPhoto={canUploadPhoto}
      canClose={canClose}
      onChanged={() => {
        load();
        onChanged();
      }}
    />
  );
}

function WorkOrderDetail({
  wo,
  technicians,
  canAssign,
  canUpdateProgress,
  canRecordRepair,
  canVerify,
  canUploadPhoto,
  canClose,
  onChanged,
}: {
  wo: WorkOrderDetailType;
  technicians: Technician[];
  canAssign: boolean;
  canUpdateProgress: boolean;
  canRecordRepair: boolean;
  canVerify: boolean;
  canUploadPhoto: boolean;
  canClose: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rootCause, setRootCause] = useState(wo.root_cause ?? "");
  const [correctiveAction, setCorrectiveAction] = useState(wo.corrective_action ?? "");

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 text-sm">
      {error && <p className="text-red-600">{error}</p>}
      <p>
        <span className="text-slate-500">Location:</span> {wo.plant_name ?? "-"} / {wo.unit_name ?? "-"}
        {(wo.tag_no || wo.asset_name) && <> / {wo.tag_no ?? wo.asset_name}</>}
      </p>
      <p>
        <span className="text-slate-500">Problem:</span> {wo.problem_description ?? "-"}
      </p>
      <div className="flex gap-2">
        <Badge value={wo.wo_type} />
        <Badge value={wo.priority} />
        <Badge value={wo.status} />
      </div>

      {wo.checklist.length > 0 && (
        <PmChecklistSection key={wo.id} wo={wo} canEdit={canUpdateProgress || canRecordRepair} onSaved={onChanged} />
      )}

      {canUpdateProgress && wo.status === "ASSIGNED" && (
        <button disabled={busy} className="btn-primary" onClick={() => call(() => api.post(`/api/work-orders/${wo.id}/start`))}>
          Start Job
        </button>
      )}

      {canRecordRepair && wo.status === "IN_PROGRESS" && (
        <div className="space-y-2 rounded-md border border-slate-200 dark:border-industrial-700 p-3">
          <textarea className="input-field" placeholder="Root cause" value={rootCause} onChange={(e) => setRootCause(e.target.value)} />
          <textarea className="input-field" placeholder="Corrective action" value={correctiveAction} onChange={(e) => setCorrectiveAction(e.target.value)} />
          <button
            disabled={busy}
            className="btn-primary"
            onClick={() => call(() => api.post(`/api/work-orders/${wo.id}/complete`, { rootCause, correctiveAction }))}
          >
            Mark Completed
          </button>
        </div>
      )}

      {canVerify && wo.status === "COMPLETED" && (
        <button disabled={busy} className="btn-secondary" onClick={() => call(() => api.post(`/api/work-orders/${wo.id}/verify`, {}))}>
          Verify Repair
        </button>
      )}

      {canClose && (wo.status === "VERIFIED" || wo.status === "COMPLETED") && (
        <button disabled={busy} className="btn-primary" onClick={() => call(() => api.post(`/api/work-orders/${wo.id}/close`))}>
          Close Work Order
        </button>
      )}

      {canAssign && (wo.status === "OPEN" || wo.status === "ASSIGNED") && (
        <AssignForm woId={wo.id} technicians={technicians} onAssigned={onChanged} />
      )}

      <AttachmentsSection woId={wo.id} images={wo.images} canUpload={canUploadPhoto} onUploaded={onChanged} />
    </div>
  );
}

type ChecklistRow = { resultValue: string; status: string; remarks: string };
const STATUS_OPTIONS = ["", "OK", "NG", "NA"];

/** Editable Result / Status(OK-NG-NA) / Remark grid for a PM's checklist,
 *  grouped by equipment (matching the plant's paper daily-checklist format),
 *  with a "Save Results" action and a Print button that opens a purpose-built
 *  printable layout (header fields + grouped table + signature block) rather
 *  than relying on window.print() against the live app screen. */
function PmChecklistSection({ wo, canEdit, onSaved }: { wo: WorkOrderDetailType; canEdit: boolean; onSaved: () => void }) {
  const [rows, setRows] = useState<Record<number, ChecklistRow>>(() => {
    const init: Record<number, ChecklistRow> = {};
    for (const item of wo.checklist) {
      init[item.id] = {
        resultValue: item.result_value ?? "",
        status: item.result_status ?? (item.is_pass === 1 ? "OK" : item.is_pass === 0 ? "NG" : ""),
        remarks: item.result_remarks ?? "",
      };
    }
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const groups = useMemo(() => {
    const byGroup = new Map<string, typeof wo.checklist>();
    for (const item of wo.checklist) {
      const g = item.equipment_group?.trim() || "General";
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(item);
    }
    return Array.from(byGroup.entries());
  }, [wo.checklist]);

  const setRow = (itemId: number, patch: Partial<ChecklistRow>) => {
    setRows((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedOk(false);
    try {
      const results = wo.checklist.map((item) => ({
        pmChecklistId: item.id,
        resultValue: rows[item.id]?.resultValue || undefined,
        status: rows[item.id]?.status || undefined,
        remarks: rows[item.id]?.remarks || undefined,
      }));
      await api.post(`/api/work-orders/${wo.id}/pm-results`, { results });
      setSavedOk(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save results");
    } finally {
      setSaving(false);
    }
  };

  const print = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const dateStr = new Date(wo.created_at).toLocaleDateString();
    const groupsHtml = groups
      .map(
        ([group, items]) => `
        <tr class="group-row"><td colspan="7">${escapeHtml(group)}</td></tr>
        ${items
          .map(
            (item, idx) => `
          <tr>
            <td class="c">${idx + 1}</td>
            <td>${escapeHtml(item.checklist_item)}</td>
            <td>${escapeHtml(item.expected_value ?? "-")}</td>
            <td>${escapeHtml(rows[item.id]?.resultValue ?? "")}</td>
            <td class="c">${escapeHtml(rows[item.id]?.status ?? "")}</td>
            <td>${escapeHtml(rows[item.id]?.remarks ?? "")}</td>
          </tr>`
          )
          .join("")}`
      )
      .join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(wo.wo_number)} - PM Checklist</title>
      <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; margin: 24px; }
        h1 { font-size: 16px; margin: 0 0 2px; }
        h2 { font-size: 12px; font-weight: normal; margin: 0 0 12px; color: #444; }
        .header-fields { display: flex; gap: 24px; margin-bottom: 12px; font-size: 12px; }
        .header-fields div { border-bottom: 1px solid #999; padding: 2px 4px; min-width: 140px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th, td { border: 1px solid #666; padding: 4px 6px; font-size: 11px; text-align: left; vertical-align: top; }
        th { background: #eee; text-align: center; }
        td.c { text-align: center; }
        tr.group-row td { background: #ddd; font-weight: bold; }
        .sign { display: flex; justify-content: space-between; margin-top: 40px; }
        .sign div { width: 30%; text-align: center; font-size: 11px; }
        .sign div .line { border-top: 1px solid #333; margin-top: 36px; padding-top: 4px; }
        @media print { body { margin: 8mm; } }
      </style></head><body>
      <h1>PM Checklist</h1>
      <h2>Work Order ${escapeHtml(wo.wo_number)}</h2>
      <div class="header-fields">
        <div>System / Unit: ${escapeHtml(wo.plant_name ?? "-")} / ${escapeHtml(wo.unit_name ?? "-")}${wo.tag_no ? " / " + escapeHtml(wo.tag_no) : ""}</div>
        <div>Date: ${escapeHtml(dateStr)}</div>
        <div>Operator: ${escapeHtml(wo.technician_name ?? "________________")}</div>
      </div>
      <table>
        <thead><tr><th style="width:28px">No.</th><th>Inspection Item</th><th style="width:110px">Standard / Spec</th><th style="width:90px">Result</th><th style="width:60px">Status<br/>(OK/NG/NA)</th><th style="width:120px">Remark</th></tr></thead>
        <tbody>${groupsHtml}</tbody>
      </table>
      <div class="sign">
        <div><div class="line">Technician</div></div>
        <div><div class="line">Supervisor</div></div>
        <div><div class="line">Engineer</div></div>
      </div>
      <script>window.onload = () => window.print();</script>
      </body></html>`);
    win.document.close();
  };

  return (
    <div className="rounded-md border border-slate-200 dark:border-industrial-700 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold">PM Checklist</h4>
        <button className="btn-secondary text-xs" onClick={print}>
          Print Checklist
        </button>
      </div>

      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
      {savedOk && !error && <p className="mb-2 text-xs text-green-600">Results saved.</p>}

      <div className="space-y-4">
        {groups.map(([group, items]) => (
          <div key={group}>
            <p className="mb-1 text-xs font-semibold uppercase text-slate-500">{group}</p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] border-collapse text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-industrial-700 text-left text-slate-500">
                    <th className="py-1 pr-2 font-medium">Item</th>
                    <th className="py-1 pr-2 font-medium">Standard / Spec</th>
                    <th className="py-1 pr-2 font-medium">Result</th>
                    <th className="py-1 pr-2 font-medium">Status</th>
                    <th className="py-1 pr-2 font-medium">Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 dark:border-industrial-800">
                      <td className="py-1 pr-2">
                        {item.checklist_item}
                        {item.is_mandatory ? <span className="text-red-500"> *</span> : null}
                      </td>
                      <td className="py-1 pr-2 text-slate-500">{item.expected_value ?? "-"}</td>
                      <td className="py-1 pr-2">
                        <input
                          className="input-field py-1 text-xs"
                          disabled={!canEdit}
                          value={rows[item.id]?.resultValue ?? ""}
                          onChange={(e) => setRow(item.id, { resultValue: e.target.value })}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <select
                          className="input-field py-1 text-xs"
                          disabled={!canEdit}
                          value={rows[item.id]?.status ?? ""}
                          onChange={(e) => setRow(item.id, { status: e.target.value })}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s || "-"}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          className="input-field py-1 text-xs"
                          disabled={!canEdit}
                          value={rows[item.id]?.remarks ?? ""}
                          onChange={(e) => setRow(item.id, { remarks: e.target.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {canEdit && (
        <button disabled={saving} className="btn-primary mt-3 text-xs" onClick={save}>
          {saving ? "Saving…" : "Save Results"}
        </button>
      )}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}

function AttachmentsSection({
  woId,
  images,
  canUpload,
  onUploaded,
}: {
  woId: number;
  images: WorkOrderDetailType["images"];
  canUpload: boolean;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "work-orders");
      const uploaded = await api.upload<{ r2Key: string; fileUrl: string; fileName: string }>("/api/upload", formData);
      await api.post(`/api/work-orders/${woId}/images`, { r2Key: uploaded.r2Key, fileUrl: uploaded.fileUrl, imageType: "GENERAL" });
      setFile(null);
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-t border-slate-200 dark:border-industrial-700 pt-3">
      <h4 className="mb-2 text-sm font-semibold">Attachments (checklist / photos)</h4>
      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}

      {images.length > 0 ? (
        <ul className="mb-3 space-y-1">
          {images.map((img) => (
            <li key={img.id}>
              <a href={img.file_url ?? "#"} target="_blank" rel="noreferrer" className="text-accent-500 hover:underline text-sm">
                {img.image_type} - {img.file_url?.split("/").pop() ?? img.r2_key}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3 text-xs text-slate-400">No files uploaded yet.</p>
      )}

      {canUpload && (
        <div className="flex items-center gap-2">
          <input
            className="input-field"
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button disabled={!file || uploading} className="btn-secondary whitespace-nowrap" onClick={upload}>
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      )}
    </div>
  );
}

function AssignForm({ woId, technicians, onAssigned }: { woId: number; technicians: Technician[]; onAssigned: () => void }) {
  const [technicianId, setTechnicianId] = useState("");
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    if (!technicianId) return;
    setBusy(true);
    try {
      await api.post(`/api/work-orders/${woId}/assign`, { technicianId: Number(technicianId) });
      onAssigned();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-2 rounded-md border border-slate-200 dark:border-industrial-700 p-3">
      <select className="input-field" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
        <option value="">Select technician</option>
        {technicians.map((t) => (
          <option key={t.id} value={t.id}>
            {t.full_name}
            {t.dept_name ? ` (${t.dept_name})` : ""}
          </option>
        ))}
      </select>
      <button disabled={busy || !technicianId} className="btn-secondary whitespace-nowrap" onClick={assign}>
        Assign
      </button>
    </div>
  );
}
